import { useEffect, useMemo, useState } from "react";
import DSAutocomplete from "../../system-design/DSAutocomplete";
import { useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import adminApiClient from "../../../api/admin";
import { ClocksListResponse } from "../../../Types";
import DSTextField from "../../system-design/DSTextField";
import DSChip from "../../system-design/DSChip";
import DSTooltip from "../../system-design/DSTooltip";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";

const VISIBLE_CHIPS = 2;

type DeviceOption = Pick<ClocksListResponse, "id" | "idf">;

type DeviceIdsFilterProps = {
  ids: string[];
  onChange: (ids: string[]) => void;
  knownDevices?: DeviceOption[];
};

const DeviceIdsFilter = ({
  ids,
  onChange,
  knownDevices,
}: DeviceIdsFilterProps) => {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [labels, setLabels] = useState<Record<string, string>>({});

  const updateSearch = useMemo(() => debounce(setSearch, 300), []);
  useEffect(() => () => updateSearch.cancel(), [updateSearch]);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["device-ids-filter", search],
    queryFn: () =>
      adminApiClient.getClocks({
        skip: 0,
        limit: 0,
        query: search,
        fetchAll: true,
      }),
    enabled: search.trim().length > 0,
  });

  const value: DeviceOption[] = useMemo(
    () =>
      ids.map((id) => ({
        id,
        idf:
          labels[id] ??
          knownDevices?.find((device) => device.id === id)?.idf ??
          id,
      })),
    [ids, labels, knownDevices],
  );

  return (
    <DSAutocomplete
      multiple
      size="small"
      sx={{
        width: 340,
        "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
          minWidth: 100,
        },
      }}
      options={options as DeviceOption[]}
      value={value}
      inputValue={input}
      loading={isFetching}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.idf || option.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      noOptionsText={search ? "No devices found" : "Type an IDF"}
      onInputChange={(_, next) => {
        setInput(next);
        updateSearch(next);
      }}
      onChange={(_, selected) => {
        setLabels((prev) => ({
          ...prev,
          ...Object.fromEntries(selected.map((d) => [d.id, d.idf])),
        }));
        onChange(selected.map((d) => d.id));
      }}
      renderTags={(selected, getTagProps) => {
        const hidden = selected.slice(VISIBLE_CHIPS);
        return (
          <>
            {selected.slice(0, VISIBLE_CHIPS).map((device, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <DSChip
                  key={key}
                  size="small"
                  label={device.idf}
                  {...tagProps}
                />
              );
            })}
            {hidden.length > 0 && (
              <DSTooltip
                placement="bottom-start"
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: 1,
                      borderColor: "divider",
                      boxShadow: 3,
                      p: 1.5,
                      maxWidth: 360,
                    },
                  },
                }}
                title={
                  <>
                    <DSTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={1}
                    >
                      {hidden.length} more selected
                    </DSTypography>
                    <DSBox display="flex" flexWrap="wrap" gap={0.75}>
                      {hidden.map((device) => (
                        <DSChip
                          key={device.id}
                          size="small"
                          label={device.idf}
                          onDelete={() =>
                            onChange(ids.filter((id) => id !== device.id))
                          }
                        />
                      ))}
                    </DSBox>
                  </>
                }
              >
                <span>
                  <DSChip
                    size="small"
                    variant="outlined"
                    label={`+${hidden.length} clocks`}
                  />
                </span>
              </DSTooltip>
            )}
          </>
        );
      }}
      renderInput={(params) => (
        <DSTextField {...params} label="Devices" placeholder="Search IDF" />
      )}
    />
  );
};

export default DeviceIdsFilter;
